import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Modal,
  Typography,
  IconButton,
  Chip,
  Divider,
  Tabs,
  Tab,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { ThemePalette } from 'mui.theme';
import AiAnalyticsAPI from 'api/ai-analytics.api';
import FeedbackWidget from 'components/FeedbackWidget';
import { CustomMuiThemeProvider } from 'theme/CustomMuiThemeProvider';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

// ─── Types ───────────────────────────────────────────────────────────────────

interface BenchmarkEntry {
  tpd?: number;
  ehpd?: number;
  unit: string;
}

interface AiAnalysisData {
  meta: {
    period: string;
    total_days: number;
    benchmark: {
      haul_truck: BenchmarkEntry;
      loader: BenchmarkEntry;
    };
  };
  fleet_verdicts: {
    haul_trucks: {
      production_realised_tons: number;
      estimated_potential_tons: number;
      utilisation_pct: number;
      summary: string;
    };
    loaders: {
      total_engine_hours: number;
      total_fuel_gal: number;
      summary: string;
    };
  };
  assets: AssetAnalysis[];
}

interface AssetAnalysis {
  bumper_id: string;
  device: string;
  make: string;
  model: string;
  asset_type: 'haul_truck' | 'loader';
  rank: number;
  score: number;
  grade: string;
  score_breakdown: {
    primary_metric_value: number;
    benchmark_value: number;
    penalty_primary: number;
    penalty_avail: number;
    penalty_other: number;
    data_penalty: number;
    total_penalty: number;
  };
  metrics: Record<string, any>;
  scoring_reason: string;
  anomalies: Array<{
    severity: 'critical' | 'warning' | 'info';
    title: string;
    detail: string;
    metric: { label: string; value: string; peers_avg?: string };
  }>;
  improvement_plan: Array<{
    priority: number;
    title: string;
    body: string;
    tags: string[];
    impact_score: number;
    impact_level: 'high' | 'medium' | 'low';
    category: string;
  }>;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const DIAG_STEPS = [
  'Gathering fleet telemetry…',
  'Analysing haul truck payload rates…',
  'Calculating loader bucket fill factors…',
  'Evaluating cycle time efficiency…',
  'Scoring asset utilisation…',
  'Detecting production anomalies…',
  'Benchmarking shift performance…',
  'Ranking asset contributors…',
  'Compiling fleet verdicts…',
  'Generating action recommendations…',
];

const LOADING_INSIGHTS = [
  {
    severity: 'info' as const,
    stat: '73%',
    headline: 'Average Fleet Utilisation',
    detail:
      'Fleet utilisation is within acceptable range. Focus on the bottom-quartile haulers to lift the average.',
  },
  {
    severity: 'warning' as const,
    stat: '–18%',
    headline: 'Payload Below Target',
    detail:
      'Several haul trucks are running consistently under target payload weight, reducing overall tonne-km output.',
  },
  {
    severity: 'info' as const,
    stat: '4.2 min',
    headline: 'Avg Queue Wait Time',
    detail:
      'Queue wait times at the loader face are elevated. Dispatching adjustments could recover 6–8 cycles per shift.',
  },
  {
    severity: 'warning' as const,
    stat: '61%',
    headline: 'Loader Efficiency Score',
    detail:
      'Loader bucket fill rates are below benchmark. Operator coaching or dig-face repositioning is recommended.',
  },
  {
    severity: 'info' as const,
    stat: 'Top 3',
    headline: 'Assets Driving 40% of Output',
    detail:
      'A small group of high-performing assets accounts for a disproportionate share of total production.',
  },
];

const getGradeColors = (score: number, dark: boolean) => {
  if (score >= 85)
    return {
      text: '#27500A',
      bg: dark ? 'rgba(99,153,34,0.18)' : '#EAF3DE',
      bar: '#639922',
    };
  if (score >= 75)
    return {
      text: '#0F6E56',
      bg: dark ? 'rgba(29,158,117,0.18)' : '#E1F5EE',
      bar: '#1D9E75',
    };
  if (score >= 65)
    return {
      text: '#0C447C',
      bg: dark ? 'rgba(55,138,221,0.18)' : '#E6F1FB',
      bar: '#378ADD',
    };
  if (score >= 50)
    return {
      text: '#854F0B',
      bg: dark ? 'rgba(239,159,39,0.18)' : '#FAEEDA',
      bar: '#EF9F27',
    };
  if (score >= 35)
    return {
      text: '#A32D2D',
      bg: dark ? 'rgba(226,75,74,0.18)' : '#FCEBEB',
      bar: '#E24B4A',
    };
  return {
    text: '#888780',
    bg: dark ? 'rgba(136,135,128,0.18)' : '#F1EFE8',
    bar: '#888780',
  };
};

const SEVERITY_COLORS = {
  critical: '#E24B4A',
  warning: '#EF9F27',
  info: '#378ADD',
};

const IMPACT_COLORS = {
  high: '#E24B4A',
  medium: '#EF9F27',
  low: '#378ADD',
};

const TAG_COLORS: Record<string, { bg: string; text: string }> = {
  operations: { bg: '#E6F1FB', text: '#0C447C' },
  data: { bg: '#FAEEDA', text: '#854F0B' },
  fuel: { bg: '#E1F5EE', text: '#0F6E56' },
  maintenance: { bg: '#FCEBEB', text: '#A32D2D' },
  scheduling: { bg: '#EAF3DE', text: '#27500A' },
  capex: { bg: '#F1EFE8', text: '#5F5E5A' },
};

// ─── Sub-components ───────────────────────────────────────────────────────────

function SeverityIcon({
  severity,
}: {
  severity: 'critical' | 'warning' | 'info';
}) {
  const color = SEVERITY_COLORS[severity];
  if (severity === 'critical')
    return <ErrorOutlineIcon sx={{ fontSize: 14, color, mt: '1px' }} />;
  if (severity === 'warning')
    return <WarningAmberIcon sx={{ fontSize: 14, color, mt: '1px' }} />;
  return <InfoOutlinedIcon sx={{ fontSize: 14, color, mt: '1px' }} />;
}

function MetricGrid({
  metrics,
  assetType,
  enableDarkTheme,
}: {
  metrics: Record<string, any>;
  assetType: 'haul_truck' | 'loader';
  enableDarkTheme: boolean;
}) {
  const { t } = useTranslation();
  const cellBg = enableDarkTheme ? 'rgba(255,255,255,0.04)' : '#F5F7FA';
  const labelColor = enableDarkTheme ? '#8A92A0' : '#616161';
  const valueColor = enableDarkTheme ? '#fff' : '#212121';

  const rows =
    assetType === 'haul_truck'
      ? [
          {
            label: t('total_production'),
            value: metrics.total_production_tons
              ? `${metrics.total_production_tons.toLocaleString()} t`
              : '—',
          },
          {
            label: t('active_days'),
            value:
              metrics.active_days != null
                ? `${metrics.active_days}/${metrics.total_days}`
                : '—',
          },
          {
            label: t('avg_active_day'),
            value: metrics.avg_tons_per_active_day
              ? `${metrics.avg_tons_per_active_day.toLocaleString()} t`
              : '—',
          },
          {
            label: t('fuel_efficiency'),
            value: metrics.avg_fuel_efficiency_tpg
              ? `${metrics.avg_fuel_efficiency_tpg} t/gal`
              : '—',
          },
          {
            label: t('peak_day'),
            value: metrics.peak_day?.date
              ? `${new Date(metrics.peak_day.date).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                })} · ${metrics.peak_day.tons?.toLocaleString()}t`
              : '—',
          },
          {
            label: t('vs_benchmark'),
            value:
              metrics.vs_benchmark_pct != null
                ? `${metrics.vs_benchmark_pct > 0 ? '+' : ''}${
                    metrics.vs_benchmark_pct
                  }%`
                : '—',
          },
        ]
      : [
          {
            label: t('engine_hours_2'),
            value: metrics.total_engine_hours
              ? `${metrics.total_engine_hours} hrs`
              : '—',
          },
          {
            label: t('avg_hrs_day'),
            value: metrics.avg_hours_per_day
              ? `${metrics.avg_hours_per_day} hrs`
              : '—',
          },
          {
            label: t('total_fuel_label'),
            value: metrics.total_fuel_gal
              ? `${metrics.total_fuel_gal.toLocaleString()} gal`
              : '—',
          },
          {
            label: t('fuel_hr'),
            value: metrics.fuel_per_hour_gal
              ? `${metrics.fuel_per_hour_gal} gal/hr`
              : '—',
          },
          {
            label: t('zero_days'),
            value:
              metrics.zero_days != null
                ? `${metrics.zero_days}/${metrics.total_days}`
                : '—',
          },
          {
            label: t('vs_benchmark'),
            value:
              metrics.vs_benchmark_pct != null
                ? `${metrics.vs_benchmark_pct > 0 ? '+' : ''}${
                    metrics.vs_benchmark_pct
                  }%`
                : '—',
          },
        ];

  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '6px',
      }}
    >
      {rows.map((row) => (
        <Box
          key={row.label}
          sx={{ background: cellBg, borderRadius: '6px', p: '8px 10px' }}
        >
          <Typography sx={{ fontSize: '10px', color: labelColor, mb: '2px' }}>
            {row.label}
          </Typography>
          <Typography
            sx={{ fontSize: '13px', fontWeight: 500, color: valueColor }}
          >
            {row.value}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

function AssetCard({
  asset,
  enableDarkTheme,
  period,
}: {
  asset: AssetAnalysis;
  enableDarkTheme: boolean;
  period: string;
}) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [tab, setTab] = useState(0);
  const grade = getGradeColors(asset.score, enableDarkTheme);

  const borderColor = enableDarkTheme ? '#2F445E' : '#E0E4EC';
  const cardBg = enableDarkTheme ? 'rgba(255,255,255,0.02)' : '#FFFFFF';
  const expandBg = enableDarkTheme ? 'rgba(255,255,255,0.03)' : '#F8F9FB';
  const textPrimary = enableDarkTheme ? '#fff' : '#212121';
  const textSecondary = enableDarkTheme ? '#8A92A0' : '#616161';
  const tabIndicator = '#5E85F0';

  const TAB_NAMES = [
    'Metrics',
    'Scoring Reason',
    'Anomalies',
    'Action Plan',
  ] as const;

  const buildAssetMetadata = (): Record<string, string> => {
    const base: Record<string, string> = {
      context: 'assetVerdict',
      bumper_id: asset.bumper_id,
      device: asset.device,
      make: asset.make,
      model: asset.model,
      asset_type: asset.asset_type,
      grade: asset.grade,
      score: String(asset.score),
      rank: String(asset.rank),
      period,
      active_tab: TAB_NAMES[tab],
    };

    if (tab === 0) {
      return { ...base, metrics: JSON.stringify(asset.metrics) };
    }
    if (tab === 1) {
      return { ...base, scoring_reason: asset.scoring_reason };
    }
    if (tab === 2) {
      return {
        ...base,
        anomalies: JSON.stringify(
          asset.anomalies.map((a) => ({
            title: a.title,
            detail: a.detail,
            severity: a.severity,
          }))
        ),
      };
    }
    return {
      ...base,
      improvement_plan: JSON.stringify(
        asset.improvement_plan.map((p) => ({
          title: p.title,
          body: p.body,
          priority: String(p.priority),
          impact_level: p.impact_level,
        }))
      ),
    };
  };

  return (
    <Box
      sx={{
        border: `1px solid ${borderColor}`,
        borderRadius: '10px',
        mb: '8px',
        overflow: 'hidden',
        background: cardBg,
        transition: 'box-shadow 0.2s',
        '&:hover': {
          boxShadow: enableDarkTheme
            ? '0 2px 12px rgba(0,0,0,0.4)'
            : '0 2px 8px rgba(0,0,0,0.08)',
        },
      }}
    >
      {/* Card top row */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          px: '15px',
          py: '12px',
          cursor: 'pointer',
          '&:hover': {
            background: enableDarkTheme
              ? 'rgba(255,255,255,0.03)'
              : 'rgba(0,0,0,0.02)',
          },
        }}
        onClick={() => {
          setExpanded((p) => !p);
        }}
      >
        {/* Rank */}
        <Typography
          sx={{
            fontSize: '20px',
            fontWeight: 500,
            color: grade.bar,
            minWidth: '24px',
          }}
        >
          {asset.rank}
        </Typography>

        {/* Info */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              flexWrap: 'wrap',
            }}
          >
            <Typography
              sx={{ fontSize: '14px', fontWeight: 600, color: textPrimary }}
            >
              {asset.bumper_id}
            </Typography>
            <Typography sx={{ fontSize: '11px', color: textSecondary }}>
              {asset.make} {asset.model}
            </Typography>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '5px',
                fontSize: '11px',
                fontWeight: 700,
                px: '8px',
                py: '2px',
                borderRadius: '20px',
                background: grade.bg,
                color: grade.text,
                border: `1px solid ${grade.bar}55`,
              }}
            >
              <Box
                sx={{
                  width: 14,
                  height: 14,
                  borderRadius: '50%',
                  background: grade.bar,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '9px',
                  fontWeight: 800,
                  color: '#fff',
                  flexShrink: 0,
                }}
              >
                {asset.grade}
              </Box>
            </Box>
            {asset.anomalies.some((a) => a.severity === 'critical') && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                <ErrorOutlineIcon
                  sx={{ fontSize: 13, color: SEVERITY_COLORS.critical }}
                />
              </Box>
            )}
          </Box>

          {/* Score bar */}
          <Box
            sx={{
              height: '4px',
              background: enableDarkTheme
                ? 'rgba(255,255,255,0.08)'
                : '#E8EBF0',
              borderRadius: '2px',
              mt: '7px',
              overflow: 'hidden',
              maxWidth: '200px',
            }}
          >
            <Box
              sx={{
                height: '100%',
                width: `${asset.score}%`,
                borderRadius: '2px',
                background: grade.bar,
                transition: 'width 0.6s ease',
              }}
            />
          </Box>
        </Box>

        {/* Score block */}
        <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
          <Typography
            sx={{
              fontSize: '26px',
              fontWeight: 500,
              color: grade.bar,
              lineHeight: 1,
            }}
          >
            {asset.score}
          </Typography>
          <Typography sx={{ fontSize: '11px', color: textSecondary }}>
            {t('100')}
          </Typography>
        </Box>

        {/* Chevron */}
        <Box sx={{ color: textSecondary, flexShrink: 0 }}>
          {expanded ? (
            <ExpandLessIcon fontSize="small" />
          ) : (
            <ExpandMoreIcon fontSize="small" />
          )}
        </Box>
      </Box>

      {/* Expanded content */}
      {expanded && (
        <Box
          sx={{ borderTop: `1px solid ${borderColor}`, background: expandBg }}
        >
          <Tabs
            value={tab}
            onChange={(_, v) => {
              setTab(v);
            }}
            variant="fullWidth"
            sx={{
              borderBottom: `1px solid ${borderColor}`,
              minHeight: '36px',
              '& .MuiTab-root': {
                fontSize: '12px',
                minHeight: '36px',
                py: 0,
                color: textSecondary,
                textTransform: 'none',
              },
              '& .Mui-selected': {
                color: `${textPrimary} !important`,
                fontWeight: 600,
              },
              '& .MuiTabs-indicator': { backgroundColor: tabIndicator },
            }}
          >
            <Tab label={t('metrics')} />
            <Tab label={t('scoring_reason')} />
            <Tab label={`Anomalies (${asset.anomalies.length})`} />
            <Tab label={`Action Plan (${asset.improvement_plan.length})`} />
          </Tabs>

          <Box sx={{ p: '13px 15px' }}>
            {tab === 0 && (
              <MetricGrid
                metrics={asset.metrics}
                assetType={asset.asset_type}
                enableDarkTheme={enableDarkTheme}
              />
            )}

            {tab === 1 && (
              <Box
                sx={{
                  background: enableDarkTheme
                    ? 'rgba(255,255,255,0.04)'
                    : '#F5F7FA',
                  borderRadius: '7px',
                  p: '10px 12px',
                  fontSize: '12px',
                  color: textSecondary,
                  lineHeight: 1.7,
                }}
              >
                {asset.scoring_reason}
              </Box>
            )}

            {tab === 2 && (
              <Box>
                {asset.anomalies.length === 0 ? (
                  <Typography sx={{ fontSize: '12px', color: textSecondary }}>
                    {t('no_anomalies_detected')}
                  </Typography>
                ) : (
                  asset.anomalies.map((a, i) => (
                    <Box
                      key={i}
                      sx={{
                        display: 'flex',
                        gap: '8px',
                        mb: '8px',
                        alignItems: 'flex-start',
                      }}
                    >
                      <SeverityIcon severity={a.severity} />
                      <Box>
                        <Typography
                          sx={{
                            fontSize: '12px',
                            fontWeight: 600,
                            color: textPrimary,
                          }}
                        >
                          {a.title}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '12px',
                            color: textSecondary,
                            lineHeight: 1.6,
                            mt: '1px',
                          }}
                        >
                          {a.detail}
                        </Typography>
                        <Box
                          sx={{
                            display: 'inline-block',
                            mt: '3px',
                            fontSize: '11px',
                            background: enableDarkTheme
                              ? 'rgba(255,255,255,0.06)'
                              : '#EBEBEB',
                            px: '7px',
                            py: '1px',
                            borderRadius: '4px',
                            color: textSecondary,
                          }}
                        >
                          {a.metric.label}: {a.metric.value}
                          {a.metric.peers_avg != null &&
                            ` · peers avg: ${a.metric.peers_avg}`}
                        </Box>
                      </Box>
                    </Box>
                  ))
                )}
              </Box>
            )}

            {tab === 3 && (
              <Box>
                {asset.improvement_plan.map((plan, i) => (
                  <Box
                    key={i}
                    sx={{
                      display: 'flex',
                      gap: '10px',
                      mb: '14px',
                      alignItems: 'flex-start',
                    }}
                  >
                    <Box
                      sx={{
                        width: '22px',
                        height: '22px',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '11px',
                        fontWeight: 600,
                        flexShrink: 0,
                        mt: '1px',
                        background: IMPACT_COLORS[plan.impact_level] + '22',
                        color: IMPACT_COLORS[plan.impact_level],
                      }}
                    >
                      {plan.priority}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        sx={{
                          fontSize: '13px',
                          fontWeight: 600,
                          color: textPrimary,
                          mb: '2px',
                        }}
                      >
                        {plan.title}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '12px',
                          color: textSecondary,
                          lineHeight: 1.6,
                        }}
                      >
                        {plan.body}
                      </Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          gap: '5px',
                          flexWrap: 'wrap',
                          mt: '5px',
                        }}
                      >
                        {plan.tags.map((tag) => {
                          const tc = TAG_COLORS[tag] ?? {
                            bg: '#E8EBF0',
                            text: '#616161',
                          };
                          return (
                            <Box
                              key={tag}
                              sx={{
                                fontSize: '10px',
                                fontWeight: 500,
                                px: '7px',
                                py: '1px',
                                borderRadius: '20px',
                                background: tc.bg,
                                color: tc.text,
                              }}
                            >
                              {tag}
                            </Box>
                          );
                        })}
                      </Box>
                      {/* Impact bar */}
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          fontSize: '10px',
                          color: textSecondary,
                          mt: '6px',
                          mb: '2px',
                        }}
                      >
                        <span style={{ textTransform: 'capitalize' }}>
                          {plan.impact_level} impact
                        </span>
                        <span>{plan.impact_score}/100</span>
                      </Box>
                      <Box
                        sx={{
                          height: '3px',
                          background: enableDarkTheme
                            ? 'rgba(255,255,255,0.08)'
                            : '#E8EBF0',
                          borderRadius: '2px',
                          overflow: 'hidden',
                        }}
                      >
                        <Box
                          sx={{
                            height: '100%',
                            width: `${plan.impact_score}%`,
                            background: IMPACT_COLORS[plan.impact_level],
                            borderRadius: '2px',
                          }}
                        />
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Box>

          {/* Feedback */}
          <Box
            sx={{
              px: '15px',
              pb: '12px',
              pt: '4px',
              display: 'flex',
              justifyContent: 'flex-end',
            }}
          >
            <FeedbackWidget
              featureName="productivityAIAnalysis"
              metadata={buildAssetMetadata()}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
}

// ─── Fleet Action Plans Dialog ────────────────────────────────────────────────

function FleetActionPlansDialog({
  open,
  onClose,
  title,
  assets,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  assets: AssetAnalysis[];
}) {
  const { t } = useTranslation();
  const assetsWithPlans = assets
    .slice()
    .sort((a, b) => a.rank - b.rank)
    .filter((a) => a.improvement_plan.length > 0);

  return (
    <CustomMuiThemeProvider>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: '12px', overflow: 'hidden' } }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: '20px',
            py: '14px',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography sx={{ fontSize: '15px', fontWeight: 600 }}>
            {title}
          </Typography>
          <IconButton
            size="small"
            onClick={onClose}
            sx={{ color: 'text.secondary' }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{
            px: '20px',
            py: '16px',
            '&::-webkit-scrollbar': { width: '5px' },
            '&::-webkit-scrollbar-thumb': {
              borderRadius: '3px',
              background: 'divider',
            },
          }}
        >
          {assetsWithPlans.length === 0 ? (
            <Typography
              sx={{ fontSize: '13px', color: 'text.secondary', py: '8px' }}
            >
              {t('no_action_plan_items_across_this_fleet')}
            </Typography>
          ) : (
            assetsWithPlans.map((asset) => (
              <Box key={asset.bumper_id} sx={{ mb: '24px' }}>
                {/* Asset header */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    mb: '10px',
                    pb: '8px',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '13px',
                      fontWeight: 700,
                      color: 'text.primary',
                    }}
                  >
                    {asset.bumper_id}
                  </Typography>
                  <Typography
                    sx={{ fontSize: '11px', color: 'text.secondary' }}
                  >
                    {asset.make} {asset.model} {t('rank')}
                    {asset.rank} {t('score')} {asset.score}/100
                  </Typography>
                </Box>

                {/* Plans for this asset */}
                {asset.improvement_plan.map((plan, i) => (
                  <Box
                    key={i}
                    sx={{
                      display: 'flex',
                      gap: '12px',
                      mb: '14px',
                      alignItems: 'flex-start',
                    }}
                  >
                    <Box
                      sx={{
                        width: '22px',
                        height: '22px',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '11px',
                        fontWeight: 600,
                        flexShrink: 0,
                        mt: '1px',
                        background: IMPACT_COLORS[plan.impact_level] + '22',
                        color: IMPACT_COLORS[plan.impact_level],
                      }}
                    >
                      {plan.priority}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        sx={{
                          fontSize: '13px',
                          fontWeight: 600,
                          color: 'text.primary',
                          mb: '2px',
                        }}
                      >
                        {plan.title}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '12px',
                          color: 'text.secondary',
                          lineHeight: 1.65,
                        }}
                      >
                        {plan.body}
                      </Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          gap: '5px',
                          flexWrap: 'wrap',
                          mt: '5px',
                        }}
                      >
                        {plan.tags.map((tag) => {
                          const tc = TAG_COLORS[tag] ?? {
                            bg: '#E8EBF0',
                            text: '#616161',
                          };
                          return (
                            <Box
                              key={tag}
                              sx={{
                                fontSize: '10px',
                                fontWeight: 500,
                                px: '7px',
                                py: '1px',
                                borderRadius: '20px',
                                background: tc.bg,
                                color: tc.text,
                              }}
                            >
                              {tag}
                            </Box>
                          );
                        })}
                      </Box>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          fontSize: '10px',
                          color: 'text.secondary',
                          mt: '6px',
                          mb: '3px',
                        }}
                      >
                        <span style={{ textTransform: 'capitalize' }}>
                          {plan.impact_level} impact
                        </span>
                        <span>{plan.impact_score}/100</span>
                      </Box>
                      <Box
                        sx={{
                          height: '3px',
                          bgcolor: 'action.hover',
                          borderRadius: '2px',
                          overflow: 'hidden',
                        }}
                      >
                        <Box
                          sx={{
                            height: '100%',
                            width: `${plan.impact_score}%`,
                            background: IMPACT_COLORS[plan.impact_level],
                            borderRadius: '2px',
                          }}
                        />
                      </Box>
                      <Box
                        sx={{
                          mt: '8px',
                          display: 'flex',
                          justifyContent: 'flex-end',
                        }}
                      >
                        <FeedbackWidget
                          featureName="productivityAIAnalysis"
                          label={t('is_this_action_plan_helpful')}
                          metadata={{
                            context: 'actionPlan',
                            bumper_id: asset.bumper_id,
                            asset_type: asset.asset_type,
                            rank: String(asset.rank),
                            score: String(asset.score),
                            plan_title: plan.title,
                            plan_priority: String(plan.priority),
                            impact_level: plan.impact_level,
                            impact_score: String(plan.impact_score),
                            tags: plan.tags.join(', '),
                            category: plan.category,
                          }}
                        />
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Box>
            ))
          )}
        </DialogContent>
      </Dialog>
    </CustomMuiThemeProvider>
  );
}

// ─── Loading Screen ───────────────────────────────────────────────────────────

function LoadingScreen({ enableDarkTheme }: { enableDarkTheme: boolean }) {
  const { t } = useTranslation();
  const [diagStep, setDiagStep] = useState(0);
  const [diagVisible, setDiagVisible] = useState(true);
  const [insightIndex, setInsightIndex] = useState(0);
  const [insightVisible, setInsightVisible] = useState(true);

  useEffect(() => {
    const iv = setInterval(() => {
      setDiagVisible(false);
      setTimeout(() => {
        setDiagStep((s) => (s + 1) % DIAG_STEPS.length);
        setDiagVisible(true);
      }, 300);
    }, 1800);
    return () => {
      clearInterval(iv);
    };
  }, []);

  useEffect(() => {
    const iv = setInterval(() => {
      setInsightVisible(false);
      setTimeout(() => {
        setInsightIndex((i) => (i + 1) % LOADING_INSIGHTS.length);
        setInsightVisible(true);
      }, 400);
    }, 3000);
    return () => {
      clearInterval(iv);
    };
  }, []);

  const insight = LOADING_INSIGHTS[insightIndex];
  const severityColor = SEVERITY_COLORS[insight.severity];

  const bg = enableDarkTheme ? '#0d1f35' : '#F8FAFB';
  const surface = enableDarkTheme ? '#111c2d' : '#ffffff';
  const textPrimary = enableDarkTheme ? '#e8ecf4' : '#1a2035';
  const textSecondary = enableDarkTheme ? '#8A92A0' : '#757575';
  const accent = '#5E85F0';

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '580px',
        background: bg,
        gap: '32px',
        px: '60px',
        py: '48px',
      }}
    >
      {/* Radar scanner */}
      <Box sx={{ position: 'relative', width: 220, height: 220 }}>
        {/* Outer dashed spinning ring */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            borderRadius: '50%',
            border: `2px dashed ${accent}55`,
            '@keyframes ai-spin-slow': {
              from: { transform: 'rotate(0deg)' },
              to: { transform: 'rotate(360deg)' },
            },
            animation: 'ai-spin-slow 8s linear infinite',
          }}
        />
        {/* Middle pulsing ring */}
        <Box
          sx={{
            position: 'absolute',
            inset: '18px',
            borderRadius: '50%',
            border: `1.5px solid ${accent}44`,
            '@keyframes ai-ring-pulse': {
              '0%,100%': { opacity: 0.4, transform: 'scale(1)' },
              '50%': { opacity: 1, transform: 'scale(1.04)' },
            },
            animation: 'ai-ring-pulse 2.4s ease-in-out infinite',
          }}
        />
        {/* Inner ring */}
        <Box
          sx={{
            position: 'absolute',
            inset: '38px',
            borderRadius: '50%',
            border: `1px solid ${accent}33`,
          }}
        />
        {/* Conic-gradient radar sweep */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            borderRadius: '50%',
            background: `conic-gradient(from 0deg, transparent 300deg, ${accent}55 360deg)`,
            '@keyframes ai-radar-spin': {
              from: { transform: 'rotate(0deg)' },
              to: { transform: 'rotate(360deg)' },
            },
            animation: 'ai-radar-spin 2s linear infinite',
          }}
        />
        {/* Crosshair horizontal */}
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '10%',
            right: '10%',
            height: '1px',
            background: `${accent}33`,
            transform: 'translateY(-50%)',
          }}
        />
        {/* Crosshair vertical */}
        <Box
          sx={{
            position: 'absolute',
            left: '50%',
            top: '10%',
            bottom: '10%',
            width: '1px',
            background: `${accent}33`,
            transform: 'translateX(-50%)',
          }}
        />
        {/* Center pulsing dot */}
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            width: 10,
            height: 10,
            borderRadius: '50%',
            background: accent,
            transform: 'translate(-50%, -50%)',
            '@keyframes ai-dot-pulse': {
              '0%,100%': { boxShadow: `0 0 0 0 ${accent}88` },
              '50%': { boxShadow: `0 0 0 8px ${accent}00` },
            },
            animation: 'ai-dot-pulse 1.6s ease-in-out infinite',
          }}
        />
      </Box>

      {/* Diagnostic step */}
      <Typography
        sx={{
          fontSize: '12px',
          fontWeight: 600,
          color: accent,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          transition: 'opacity 0.3s ease',
          opacity: diagVisible ? 1 : 0,
          minHeight: '18px',
          textAlign: 'center',
        }}
      >
        {DIAG_STEPS[diagStep]}
      </Typography>

      {/* Insight card */}
      <Box
        sx={{
          width: '100%',
          maxWidth: '560px',
          borderRadius: '10px',
          overflow: 'hidden',
          background: surface,
          boxShadow: enableDarkTheme
            ? '0 2px 16px rgba(0,0,0,0.45)'
            : '0 2px 16px rgba(0,0,0,0.08)',
          transition: 'opacity 0.4s ease, transform 0.4s ease',
          opacity: insightVisible ? 1 : 0,
          transform: insightVisible ? 'translateY(0)' : 'translateY(6px)',
        }}
      >
        {/* Severity header band */}
        <Box
          sx={{
            background: severityColor,
            px: '12px',
            py: '6px',
          }}
        >
          <Typography
            sx={{
              fontSize: '10px',
              fontWeight: 700,
              color: '#fff',
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
            }}
          >
            {insight.severity} {t('fleet_insight')}
          </Typography>
        </Box>
        <Box sx={{ p: '14px 16px' }}>
          {/* Big stat */}
          <Typography
            sx={{
              fontSize: '30px',
              fontWeight: 800,
              color: severityColor,
              lineHeight: 1,
              mb: '4px',
            }}
          >
            {insight.stat}
          </Typography>
          {/* Headline */}
          <Typography
            sx={{
              fontSize: '13px',
              fontWeight: 700,
              color: textPrimary,
              mb: '6px',
            }}
          >
            {insight.headline}
          </Typography>
          {/* Detail */}
          <Typography
            sx={{
              fontSize: '12px',
              color: textSecondary,
              lineHeight: 1.6,
            }}
          >
            {insight.detail}
          </Typography>
        </Box>
      </Box>

      {/* Progress dots */}
      <Box sx={{ display: 'flex', gap: '6px' }}>
        {LOADING_INSIGHTS.map((_, i) => (
          <Box
            key={i}
            sx={{
              width: i === insightIndex ? '18px' : '6px',
              height: '6px',
              borderRadius: '3px',
              background:
                i === insightIndex
                  ? accent
                  : enableDarkTheme
                  ? 'rgba(255,255,255,0.15)'
                  : '#CBD2DC',
              transition: 'all 0.3s ease',
            }}
          />
        ))}
      </Box>
    </Box>
  );
}

// ─── Summary Cards ────────────────────────────────────────────────────────────

function SummaryCards({
  data,
  enableDarkTheme,
}: {
  data: AiAnalysisData;
  enableDarkTheme: boolean;
}) {
  const { t } = useTranslation();
  const cellBg = enableDarkTheme ? 'rgba(255,255,255,0.05)' : '#F5F7FA';
  const label = enableDarkTheme ? '#8A92A0' : '#616161';
  const value = enableDarkTheme ? '#fff' : '#212121';
  const sub = enableDarkTheme ? '#5A6370' : '#9E9E9E';

  const trucks = data.assets.filter((a) => a.asset_type === 'haul_truck');
  const topTruck = trucks.reduce(
    (best, a) => (a.score > best.score ? a : best),
    trucks[0]
  );
  const worstTruck = trucks.reduce(
    (worst, a) => (a.score < worst.score ? a : worst),
    trucks[0]
  );
  const topGrade = topTruck
    ? getGradeColors(topTruck.score, enableDarkTheme)
    : null;
  const worstGrade = worstTruck
    ? getGradeColors(worstTruck.score, enableDarkTheme)
    : null;

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: '10px',
        mb: '16px',
      }}
    >
      <Box sx={{ background: cellBg, borderRadius: '8px', p: '10px 12px' }}>
        <Typography sx={{ fontSize: '11px', color: label, mb: '3px' }}>
          {t('production_realised')}
        </Typography>
        <Typography sx={{ fontSize: '18px', fontWeight: 500, color: value }}>
          {data.fleet_verdicts.haul_trucks.production_realised_tons.toLocaleString()}{' '}
          t
        </Typography>
        <Typography sx={{ fontSize: '10px', color: sub, mt: '2px' }}>
          {data.fleet_verdicts.haul_trucks.utilisation_pct}
          {t('of_potential')}
        </Typography>
      </Box>

      <Box sx={{ background: cellBg, borderRadius: '8px', p: '10px 12px' }}>
        <Typography sx={{ fontSize: '11px', color: label, mb: '3px' }}>
          {t('loader_engine_hours')}
        </Typography>
        <Typography sx={{ fontSize: '18px', fontWeight: 500, color: value }}>
          {data.fleet_verdicts.loaders.total_engine_hours.toFixed(1)} hrs
        </Typography>
        <Typography sx={{ fontSize: '10px', color: sub, mt: '2px' }}>
          {data.assets.filter((a) => a.asset_type === 'loader').length}{' '}
          {t('loaders_combined')}
        </Typography>
      </Box>

      <Box sx={{ background: cellBg, borderRadius: '8px', p: '10px 12px' }}>
        <Typography sx={{ fontSize: '11px', color: label, mb: '3px' }}>
          {t('top_performer')}
        </Typography>
        <Typography
          sx={{
            fontSize: '18px',
            fontWeight: 500,
            color: topGrade?.bar ?? value,
          }}
        >
          {topTruck?.bumper_id ?? '—'}
        </Typography>
        <Typography sx={{ fontSize: '10px', color: sub, mt: '2px' }}>
          {topTruck
            ? `${
                topTruck.metrics.avg_tons_per_active_day?.toLocaleString() ?? ''
              } t/day · grade ${topTruck.grade} · ${topTruck.score}/100`
            : ''}
        </Typography>
      </Box>

      <Box sx={{ background: cellBg, borderRadius: '8px', p: '10px 12px' }}>
        <Typography sx={{ fontSize: '11px', color: label, mb: '3px' }}>
          {t('worst_performer')}
        </Typography>
        <Typography
          sx={{ fontSize: '18px', fontWeight: 500, color: '#E24B4A' }}
        >
          {worstTruck?.bumper_id ?? '—'}
        </Typography>
        <Typography sx={{ fontSize: '10px', color: sub, mt: '2px' }}>
          {worstTruck
            ? `${
                worstTruck.metrics.total_production_tons?.toLocaleString() ?? 0
              } t · ${
                worstTruck.score === 0
                  ? 'fully inactive'
                  : 'grade ' + worstTruck.grade
              } · ${worstTruck.score}/100`
            : ''}
        </Typography>
      </Box>
    </Box>
  );
}

// ─── Verdict Cards ────────────────────────────────────────────────────────────

function VerdictCards({
  data,
  enableDarkTheme,
}: {
  data: AiAnalysisData;
  enableDarkTheme: boolean;
}) {
  const { t } = useTranslation();
  const cardBg = enableDarkTheme ? 'rgba(255,255,255,0.03)' : '#FFFFFF';
  const border = enableDarkTheme ? '#2F445E' : '#E0E4EC';
  const textPri = enableDarkTheme ? '#fff' : '#212121';
  const textSec = enableDarkTheme ? '#8A92A0' : '#616161';

  const trucks = data.assets.filter((a) => a.asset_type === 'haul_truck');
  const loaders = data.assets.filter((a) => a.asset_type === 'loader');
  const bestTruck = trucks.reduce(
    (b, a) => (a.score > b.score ? a : b),
    trucks[0]
  );
  const worstTruck = trucks.reduce(
    (w, a) => (a.score < w.score ? a : w),
    trucks[0]
  );
  const bestLoader = loaders.reduce(
    (b, a) => (a.score > b.score ? a : b),
    loaders[0]
  );
  const worstLoader = loaders.reduce(
    (w, a) => (a.score < w.score ? a : w),
    loaders[0]
  );

  const pct = data.fleet_verdicts.haul_trucks.utilisation_pct;

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '10px',
        mb: '8px',
      }}
    >
      {/* Hauler verdict */}
      <Box
        sx={{
          background: cardBg,
          border: `1px solid ${border}`,
          borderRadius: '10px',
          p: '14px 16px',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: '10px',
          }}
        >
          <Typography
            sx={{ fontSize: '13px', fontWeight: 600, color: textPri }}
          >
            {t('hauler_fleet_verdict')}
          </Typography>
          <Chip
            label={`${pct}% utilised`}
            size="small"
            sx={{
              fontSize: '11px',
              fontWeight: 500,
              background:
                pct >= 80 ? '#EAF3DE' : pct >= 60 ? '#FAEEDA' : '#FCEBEB',
              color: pct >= 80 ? '#27500A' : pct >= 60 ? '#854F0B' : '#A32D2D',
              height: '22px',
            }}
          />
        </Box>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '6px',
            mb: '10px',
          }}
        >
          <Box
            sx={{ background: '#EAF3DE', borderRadius: '7px', p: '7px 9px' }}
          >
            <Typography sx={{ fontSize: '10px', color: '#3B6D11', mb: '1px' }}>
              {t('best_asset')}
            </Typography>
            <Typography
              sx={{ fontSize: '13px', fontWeight: 600, color: '#27500A' }}
            >
              {bestTruck?.bumper_id ?? '—'}
            </Typography>
          </Box>
          <Box
            sx={{ background: '#FCEBEB', borderRadius: '7px', p: '7px 9px' }}
          >
            <Typography sx={{ fontSize: '10px', color: '#A32D2D', mb: '1px' }}>
              {t('drag_asset')}
            </Typography>
            <Typography
              sx={{ fontSize: '13px', fontWeight: 600, color: '#A32D2D' }}
            >
              {worstTruck?.bumper_id ?? '—'}
            </Typography>
          </Box>
          <Box
            sx={{ background: '#FAEEDA', borderRadius: '7px', p: '7px 9px' }}
          >
            <Typography sx={{ fontSize: '10px', color: '#854F0B', mb: '1px' }}>
              Realised
            </Typography>
            <Typography
              sx={{ fontSize: '13px', fontWeight: 600, color: '#633806' }}
            >
              {data.fleet_verdicts.haul_trucks.production_realised_tons.toLocaleString()}{' '}
              t
            </Typography>
          </Box>
        </Box>
        <Typography sx={{ fontSize: '12px', color: textSec, lineHeight: 1.65 }}>
          {data.fleet_verdicts.haul_trucks.summary}
        </Typography>
        <Box sx={{ mt: '10px', display: 'flex', justifyContent: 'flex-end' }}>
          <FeedbackWidget
            featureName="productivityAIAnalysis"
            metadata={{
              context: 'fleetVerdict_haulTrucks',
              period: data.meta.period,
              production_realised_tons: String(
                data.fleet_verdicts.haul_trucks.production_realised_tons
              ),
              estimated_potential_tons: String(
                data.fleet_verdicts.haul_trucks.estimated_potential_tons
              ),
              utilisation_pct: String(
                data.fleet_verdicts.haul_trucks.utilisation_pct
              ),
              summary: data.fleet_verdicts.haul_trucks.summary,
            }}
          />
        </Box>
      </Box>

      {/* Loader verdict */}
      <Box
        sx={{
          background: cardBg,
          border: `1px solid ${border}`,
          borderRadius: '10px',
          p: '14px 16px',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: '10px',
          }}
        >
          <Typography
            sx={{ fontSize: '13px', fontWeight: 600, color: textPri }}
          >
            {t('loader_fleet_verdict')}
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '6px',
            mb: '10px',
          }}
        >
          <Box
            sx={{ background: '#EAF3DE', borderRadius: '7px', p: '7px 9px' }}
          >
            <Typography sx={{ fontSize: '10px', color: '#3B6D11', mb: '1px' }}>
              {t('best_loader')}
            </Typography>
            <Typography
              sx={{ fontSize: '13px', fontWeight: 600, color: '#27500A' }}
            >
              {bestLoader?.bumper_id ?? '—'}
            </Typography>
          </Box>
          <Box
            sx={{ background: '#FCEBEB', borderRadius: '7px', p: '7px 9px' }}
          >
            <Typography sx={{ fontSize: '10px', color: '#A32D2D', mb: '1px' }}>
              {t('worst_loader')}
            </Typography>
            <Typography
              sx={{ fontSize: '13px', fontWeight: 600, color: '#A32D2D' }}
            >
              {worstLoader?.bumper_id ?? '—'}
            </Typography>
          </Box>
          <Box
            sx={{ background: '#FAEEDA', borderRadius: '7px', p: '7px 9px' }}
          >
            <Typography sx={{ fontSize: '10px', color: '#854F0B', mb: '1px' }}>
              {t('fuel_burned')}
            </Typography>
            <Typography
              sx={{ fontSize: '13px', fontWeight: 600, color: '#633806' }}
            >
              {data.fleet_verdicts.loaders.total_fuel_gal.toLocaleString()} gal
            </Typography>
          </Box>
        </Box>
        <Typography sx={{ fontSize: '12px', color: textSec, lineHeight: 1.65 }}>
          {data.fleet_verdicts.loaders.summary}
        </Typography>
        <Box sx={{ mt: '10px', display: 'flex', justifyContent: 'flex-end' }}>
          <FeedbackWidget
            featureName="productivityAIAnalysis"
            metadata={{
              context: 'fleetVerdict_loaders',
              period: data.meta.period,
              total_engine_hours: String(
                data.fleet_verdicts.loaders.total_engine_hours
              ),
              total_fuel_gal: String(
                data.fleet_verdicts.loaders.total_fuel_gal
              ),
              summary: data.fleet_verdicts.loaders.summary,
            }}
          />
        </Box>
      </Box>
    </Box>
  );
}

// ─── Main Modal ───────────────────────────────────────────────────────────────

interface Props {
  open: boolean;
  onClose: () => void;
  enableDarkTheme: boolean;
}

const AiAnalysisModal = ({ open, onClose, enableDarkTheme }: Props) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState<AiAnalysisData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [haulerPlanOpen, setHaulerPlanOpen] = useState(false);
  const [loaderPlanOpen, setLoaderPlanOpen] = useState(false);

  const api = new AiAnalyticsAPI();

  const fetchAnalysis = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    setData(null);
    try {
      const result = await api.getProductivityAnalysis();
      setData(result as AiAnalysisData);
    } catch {
      setError('Failed to load AI analysis. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      void fetchAnalysis();
    }
  }, [open]);

  const bg = enableDarkTheme ? '#0d1f35' : '#F8FAFB';
  const headerBg = enableDarkTheme ? '#0a1929' : '#FFFFFF';
  const borderColor = enableDarkTheme ? '#1A3050' : '#E0E4EC';
  const textPrimary = enableDarkTheme ? '#fff' : '#212121';
  const textSecondary = enableDarkTheme ? '#8A92A0' : '#616161';

  const trucks =
    data?.assets.filter((a) => a.asset_type === 'haul_truck') ?? [];
  const loaders = data?.assets.filter((a) => a.asset_type === 'loader') ?? [];

  return (
    <>
      <Modal open={open} onClose={onClose}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 'min(1100px, 96vw)',
            maxHeight: '92vh',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: '12px',
            overflow: 'hidden',
            boxShadow: '0 24px 60px rgba(0,0,0,0.35)',
            outline: 'none',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              background: headerBg,
              borderBottom: `1px solid ${borderColor}`,
              px: '20px',
              py: '14px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexShrink: 0,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Typography
                sx={{ fontSize: '16px', fontWeight: 600, color: textPrimary }}
              >
                {t('ai_productivity_analysis')}
              </Typography>
              {data && (
                <Box
                  sx={{
                    fontSize: '11px',
                    fontWeight: 500,
                    px: '10px',
                    py: '3px',
                    borderRadius: '20px',
                    border: '0.5px solid #AFA9EC',
                    background: '#EEEDFE',
                    color: '#3C3489',
                  }}
                >
                  {data.meta.period}
                </Box>
              )}
            </Box>
            <IconButton
              size="small"
              onClick={onClose}
              sx={{ color: textSecondary }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Body */}
          <Box
            sx={{
              background: bg,
              overflowY: 'auto',
              flex: 1,
              '&::-webkit-scrollbar': { width: '6px' },
              '&::-webkit-scrollbar-track': { background: 'transparent' },
              '&::-webkit-scrollbar-thumb': {
                background: enableDarkTheme ? '#2F445E' : '#CBD2DC',
                borderRadius: '3px',
              },
            }}
          >
            {isLoading && <LoadingScreen enableDarkTheme={enableDarkTheme} />}

            {error && !isLoading && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '200px',
                  flexDirection: 'column',
                  gap: '10px',
                }}
              >
                <Typography
                  sx={{
                    color: ThemePalette.typography.danger,
                    fontSize: '14px',
                  }}
                >
                  {error}
                </Typography>
              </Box>
            )}

            {data && !isLoading && (
              <Box sx={{ p: '20px' }}>
                {/* Period subtitle */}
                <Typography
                  sx={{ fontSize: '12px', color: textSecondary, mb: '14px' }}
                >
                  {data.meta.total_days}
                  {t('day_period_click_any_card_to_expand')}
                </Typography>

                {/* Summary cards */}
                <SummaryCards data={data} enableDarkTheme={enableDarkTheme} />

                {/* Fleet verdicts */}
                <VerdictCards data={data} enableDarkTheme={enableDarkTheme} />

                {/* Haul trucks section */}
                {trucks.length > 0 && (
                  <>
                    <Divider sx={{ borderColor, my: '14px' }} />
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        mb: '10px',
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: '11px',
                          fontWeight: 600,
                          color: textSecondary,
                          textTransform: 'uppercase',
                          letterSpacing: '0.06em',
                        }}
                      >
                        {t('haul_trucks_ranked')}
                      </Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          setHaulerPlanOpen(true);
                        }}
                        sx={{
                          fontSize: '11px',
                          py: '3px',
                          px: '12px',
                          textTransform: 'none',
                          borderRadius: '20px',
                          borderColor: enableDarkTheme
                            ? 'rgba(94,133,240,0.4)'
                            : 'rgba(94,133,240,0.5)',
                          color: '#5E85F0',
                          '&:hover': {
                            borderColor: '#5E85F0',
                            background: 'rgba(94,133,240,0.08)',
                          },
                        }}
                      >
                        {t('view_all_action_plans')}
                      </Button>
                    </Box>
                    {trucks
                      .sort((a, b) => a.rank - b.rank)
                      .map((asset) => (
                        <AssetCard
                          key={asset.bumper_id}
                          asset={asset}
                          enableDarkTheme={enableDarkTheme}
                          period={data.meta.period}
                        />
                      ))}
                  </>
                )}

                {/* Loaders section */}
                {loaders.length > 0 && (
                  <>
                    <Divider sx={{ borderColor, my: '14px' }} />
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        mb: '10px',
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: '11px',
                          fontWeight: 600,
                          color: textSecondary,
                          textTransform: 'uppercase',
                          letterSpacing: '0.06em',
                        }}
                      >
                        {t('loaders_ranked')}
                      </Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          setLoaderPlanOpen(true);
                        }}
                        sx={{
                          fontSize: '11px',
                          py: '3px',
                          px: '12px',
                          textTransform: 'none',
                          borderRadius: '20px',
                          borderColor: enableDarkTheme
                            ? 'rgba(94,133,240,0.4)'
                            : 'rgba(94,133,240,0.5)',
                          color: '#5E85F0',
                          '&:hover': {
                            borderColor: '#5E85F0',
                            background: 'rgba(94,133,240,0.08)',
                          },
                        }}
                      >
                        {t('view_all_action_plans')}
                      </Button>
                    </Box>
                    {loaders
                      .sort((a, b) => a.rank - b.rank)
                      .map((asset) => (
                        <AssetCard
                          key={asset.bumper_id}
                          asset={asset}
                          enableDarkTheme={enableDarkTheme}
                          period={data.meta.period}
                        />
                      ))}
                  </>
                )}
              </Box>
            )}
          </Box>
        </Box>
      </Modal>

      <FleetActionPlansDialog
        open={haulerPlanOpen}
        onClose={() => {
          setHaulerPlanOpen(false);
        }}
        title={t('haul_trucks_all_action_plans')}
        assets={trucks}
      />
      <FleetActionPlansDialog
        open={loaderPlanOpen}
        onClose={() => {
          setLoaderPlanOpen(false);
        }}
        title={t('loaders_all_action_plans')}
        assets={loaders}
      />
    </>
  );
};

export default AiAnalysisModal;
